import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import { 
  Shield, 
  Lock, 
  Eye, 
  Server,
  Database,
  Key,
  FileCheck,
  Globe,
  CheckCircle,
  ArrowRight,
  Building2,
  Users,
  Trash2,
  Download
} from 'lucide-react';

const Security = () => {
  const certifications = [
    { name: 'SOC 2-aligned controls', status: 'Live', color: 'emerald' },
    { name: 'GDPR / CCPA rights honoured', status: 'Live', color: 'blue' },
    { name: 'SOC 2 Type II audit', status: 'Planned · customer #25', color: 'amber' },
    { name: 'ISO 27001', status: 'Post-Series-A roadmap', color: 'amber' },
  ];

  const securityFeatures = [
    {
      icon: Lock,
      title: 'Data Encryption',
      description: 'AES-256-GCM encrypts your data at rest (stored in database). TLS 1.3 encrypts data in transit (over the network). This is the same encryption used by banks and government agencies.',
    },
    {
      icon: Key,
      title: 'Zero Credential Storage',
      description: 'We NEVER persist your AWS, Azure, GCP, Snowflake, Databricks, or DigitalOcean credentials in our database. Credentials are used in-memory to run a read-only audit and then discarded — they are not written to disk or logs.',
    },
    {
      icon: Eye,
      title: 'Complete Audit Trail',
      description: 'Every action in CARTIEAI is logged with timestamps, user IDs, and IP addresses. Full audit logs are available for compliance review and can be exported anytime.',
    },
    {
      icon: Server,
      title: 'Stateless Audit Architecture',
      description: 'When you run a cloud audit, your read-only credentials are held in-memory only for the duration of the scan, then immediately discarded. We do not persist them in our database, on disk, or in logs.',
    },
    {
      icon: Users,
      title: 'Role-Based Access Control',
      description: 'Granular permissions ensure team members only see what they need. Passwords are hashed using bcrypt (one-way encryption - even we cannot see them).',
    },
    {
      icon: Trash2,
      title: 'Data Deletion Rights',
      description: 'You can export or delete all your data at any time. We honor deletion requests within 24 hours. Your data, your control.',
    },
  ];

  const encryptionDetails = [
    { label: 'Passwords', method: 'bcrypt (one-way hash)', description: 'Cannot be reversed, even by us' },
    { label: 'Data at Rest', method: 'AES-256-GCM', description: 'Military-grade encryption for stored data' },
    { label: 'Data in Transit', method: 'TLS 1.3', description: 'Latest encryption for network traffic' },
    { label: 'API Keys', method: 'Environment variables', description: 'Never stored in code or database' },
  ];

  const aiPrivacy = [
    { label: 'AI sees only aggregated summaries', included: true },
    { label: 'You control AI data access level', included: true },
    { label: 'Preview data before AI queries', included: true },
    { label: 'Option to disable AI completely', included: true },
    { label: 'AI never sees credentials', included: true },
    { label: 'No data sold to third parties', included: true },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 dark:bg-slate-950">
      <SEOHead
        title="Security & Compliance — CARTIEAI"
        description="SOC 2-aligned controls in place today, formal Type II audit scheduled at customer #25. GDPR &amp; CCPA rights honoured. AES-256 encryption at rest, TLS 1.3 in transit. Read-only cloud connections only — we never modify your infrastructure."
        canonical="https://cartieai.com/security"
        keywords="SOC 2, GDPR, CCPA, FinOps security, cloud cost data security"
      />
      {/* Header */}
      <div className="bg-emerald-600 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-6" data-testid="security-nav">
            <Link to="/" className="text-emerald-200 hover:text-white text-sm font-medium" data-testid="security-back-home">
              ← Back to home
            </Link>
            <span className="text-emerald-300/50">·</span>
            <Link to="/pricing" className="text-emerald-200/80 hover:text-white text-sm">Pricing</Link>
            <span className="text-emerald-300/50">·</span>
            <Link to="/login" className="text-emerald-200/80 hover:text-white text-sm">Sign in</Link>
          </div>
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-white/20 rounded-xl">
              <Shield className="w-10 h-10" />
            </div>
            <div>
              <h1 className="text-4xl font-bold">Security & Privacy</h1>
              <p className="text-emerald-100 mt-1">How we protect your cloud cost data</p>
            </div>
          </div>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap gap-3 mt-8">
            {certifications.map((cert) => (
              <Badge 
                key={cert.name}
                className={`bg-white/20 text-white border border-white/30 px-4 py-2`}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {cert.name}: {cert.status}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Our Commitment */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-white mb-4">
            Your Data, Your Control
          </h2>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            We understand that your cloud cost data is sensitive. That's why we've built CARTIEAI 
            with privacy-first principles. You always have complete control over your data.
          </p>
        </div>

        {/* Security Features Grid — v34.38 (Feb 2026): explicit dark
            card surfaces. shadcn `<Card>` defaults to light, so we force
            bg-slate-900/60 + light text so each tile is readable on the
            dark page bg (matches the rest of the marketing surfaces). */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {securityFeatures.map((feature, idx) => (
            <Card key={idx} className="bg-slate-900/60 backdrop-blur border border-white/10 hover:border-emerald-500/30 transition-colors">
              <CardHeader>
                <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-emerald-300" />
                </div>
                <CardTitle className="text-lg text-white">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Encryption Explained Section */}
        <div className="bg-slate-900/60 backdrop-blur rounded-2xl p-8 mb-16 border border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-emerald-500/15 rounded-xl">
              <Lock className="w-6 h-6 text-emerald-300" />
            </div>
            <h2 className="text-2xl font-bold text-white">How We Protect Your Data</h2>
          </div>
          
          <p className="text-slate-300 mb-6">
            We use industry-standard encryption methods. Here's what each one means:
          </p>

          <div className="grid gap-4">
            {encryptionDetails.map((item, idx) => (
              <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-4 bg-slate-900/60 dark:bg-slate-800 rounded-lg border border-white/10">
                <div className="flex items-center gap-3 min-w-[140px]">
                  <Shield className="w-5 h-5 text-emerald-500" />
                  <span className="font-semibold text-white">{item.label}</span>
                </div>
                <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                  <code className="text-sm bg-slate-900/60 dark:bg-slate-700 px-2 py-1 rounded text-emerald-300 dark:text-emerald-400">{item.method}</code>
                  <span className="text-slate-300 text-sm">{item.description}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-emerald-500/10 dark:bg-emerald-900/20 rounded-lg border border-emerald-500/30 dark:border-emerald-800">
            <p className="text-emerald-200 dark:text-emerald-300 text-sm">
              <strong>Bottom line:</strong> Your passwords cannot be decrypted (one-way hash). Your data is encrypted with military-grade AES-256. 
              All network traffic uses TLS 1.3 (same as online banking). Your cloud credentials never touch our servers.
            </p>
          </div>
        </div>

        {/* AI Privacy Section */}
        <div className="bg-emerald-50 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-2xl p-8 mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-emerald-500/15 dark:bg-blue-900/30 rounded-xl">
              <Eye className="w-6 h-6 text-emerald-300" />
            </div>
            <h2 className="text-2xl font-bold text-white">AI & Your Privacy</h2>
          </div>
          
          <p className="text-slate-300 mb-6 max-w-3xl">
            Our AI features are designed with privacy in mind. You have complete control over 
            what data the AI can access, and you can disable AI features entirely if you prefer.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {aiPrivacy.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-slate-900/60 dark:bg-slate-800 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <span className="text-slate-200 dark:text-slate-300">{item.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-amber-500/15 dark:bg-amber-900/30 rounded-lg border border-amber-500/30 dark:border-amber-800">
            <p className="text-amber-200 dark:text-amber-300 text-sm">
              <strong>Important:</strong> When AI features are enabled with "Limited" or "Anonymized" mode, 
              the AI only sees aggregated summaries—never your raw cost data, resource IDs, or credentials.
            </p>
          </div>
        </div>

        {/* Data Practices */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            Our Data Practices
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center mx-auto mb-4">
                  <Database className="w-8 h-8 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
                </div>
                <h3 className="font-bold text-white mb-2">We Collect</h3>
                <ul className="text-sm text-slate-300 space-y-1">
                  <li>Cloud cost data (with your permission)</li>
                  <li>Usage patterns for optimization</li>
                  <li>Account settings & preferences</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
                </div>
                <h3 className="font-bold text-white mb-2">We Protect</h3>
                <ul className="text-sm text-slate-300 space-y-1">
                  <li>AES-256 encryption at rest</li>
                  <li>TLS 1.3 encryption in transit</li>
                  <li>Regular security audits</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-bold text-white mb-2">We Never</h3>
                <ul className="text-sm text-slate-300 space-y-1">
                  <li>Sell your data to anyone</li>
                  <li>Store your cloud credentials</li>
                  <li>Share data without consent</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Your Rights */}
        <div className="bg-slate-900/60 dark:bg-slate-900 rounded-2xl p-8 mb-16">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            Your Rights
          </h2>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Eye, title: 'Right to Access', desc: 'View all data we have about you' },
              { icon: Download, title: 'Right to Export', desc: 'Download your data anytime' },
              { icon: Trash2, title: 'Right to Delete', desc: 'Request complete data deletion' },
              { icon: FileCheck, title: 'Right to Correct', desc: 'Update or correct your data' },
            ].map((right, idx) => (
              <div key={idx} className="text-center">
                <div className="w-12 h-12 rounded-xl bg-slate-900/60 dark:bg-slate-800 shadow-sm flex items-center justify-center mx-auto mb-3">
                  <right.icon className="w-6 h-6 text-emerald-300" />
                </div>
                <h3 className="font-semibold text-white mb-1">{right.title}</h3>
                <p className="text-sm text-slate-400">{right.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Questions About Security?
          </h2>
          <p className="text-slate-300 mb-6">
            Our security team is happy to answer any questions or provide additional documentation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <FileCheck className="w-4 h-4 mr-2" />
              Request Security Documentation
            </Button>
            <Button variant="outline">
              Contact Security Team
            </Button>
          </div>
          <p className="text-sm text-slate-400 mt-4">
            hello@cartieai.com
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-sm text-slate-400">
          <p data-testid="security-footer-entity">© 2026 CARTIEAI LLC · Texas, USA · All rights reserved.</p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/login" className="hover:text-emerald-300">Login</Link>
            <Link to="/pricing" className="hover:text-emerald-300">Pricing</Link>
            <Link to="/privacy-policy" className="hover:text-emerald-300">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-emerald-300">Terms of Service</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Security;
